import { useState } from 'react';
import type { CSSProperties, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { Button, Icon, Logo } from '@vault/ui';
import { api, ApiError } from '../api/client.js';
import { useT } from '../i18n/index.js';
import { CenterShell } from './shared/CenterShell.js';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Passwordless sign-in. Enter an email → we mail a magic link; the link opens
 * /auth/verify?token=…. In dev the link is printed to the API console, so we
 * also expose a "paste token" affordance that jumps straight to verify.
 */
export function LoginPage() {
  const navigate = useNavigate();
  const { t } = useT();
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [devToken, setDevToken] = useState('');

  const requestLink = useMutation({
    mutationFn: (address: string) => api.auth.requestLink(address),
    onSuccess: () => setSent(true),
  });

  const emailValid = EMAIL_RE.test(email.trim());

  function submitEmail(e: FormEvent) {
    e.preventDefault();
    if (!emailValid || requestLink.isPending) return;
    requestLink.mutate(email.trim());
  }

  function verifyToken(e: FormEvent) {
    e.preventDefault();
    const t = devToken.trim();
    if (!t) return;
    navigate(`/auth/verify?token=${encodeURIComponent(t)}`);
  }

  const errorMessage = requestLink.isError
    ? requestLink.error instanceof ApiError && requestLink.error.status === 429
      ? t('login.error.tooMany')
      : t('login.error.sendFailed')
    : null;

  return (
    <CenterShell maxWidth={400}>
      <div style={card}>
        {!sent ? (
          <>
            <header style={header}>
              <Logo size={42} style={{ borderRadius: 11 }} />
              <span style={title}>{t('login.welcome')}</span>
              <span style={{ fontSize: 13, color: '#71717A' }}>{t('login.tagline')}</span>
            </header>

            <form onSubmit={submitEmail} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <label style={fieldRow(!!email && !emailValid)}>
                <Icon name="mail" size={14} color="#71717A" />
                <input
                  type="email"
                  autoFocus
                  placeholder={t('login.emailPlaceholder')}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  style={nativeInput}
                />
              </label>
              <Button variant="primary" fullWidth loading={requestLink.isPending}>
                {t('login.sendLink')}
              </Button>
              {errorMessage && <span style={errText}>{errorMessage}</span>}
            </form>

            <DevTokenRow value={devToken} onChange={setDevToken} onSubmit={verifyToken} />

            <span style={reassure}>{t('login.reassure')}</span>
          </>
        ) : (
          <>
            <header style={{ ...header, gap: 12 }}>
              <span style={mailBadge}>
                <Icon name="mail" size={24} color="var(--accent-ink)" />
              </span>
              <span style={title}>{t('login.checkInbox')}</span>
              <span style={{ fontSize: 12.5, lineHeight: 1.6, color: 'var(--text-2)', textAlign: 'center' }}>
                {t('login.sentTo')}
                <br />
                <b style={{ color: 'var(--text-1)', fontFamily: 'var(--font-mono)', fontSize: 12, fontWeight: 600 }}>
                  {email.trim()}
                </b>
              </span>
              <span style={expiryPill}>
                <span style={{ width: 5, height: 5, borderRadius: '50%', background: 'var(--accent)' }} />
                {t('login.expires')}
              </span>
            </header>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <span style={{ fontSize: 12, color: 'var(--text-2)', textAlign: 'center' }}>
                {t('login.nothingThere')}{' '}
                <b
                  onClick={() => !requestLink.isPending && requestLink.mutate(email.trim())}
                  style={{ color: 'var(--primary)', cursor: 'pointer', fontWeight: 600 }}
                >
                  {t('common.resend')}
                </b>{' '}
                · {t('login.checkSpam')}
              </span>
              <span
                onClick={() => setSent(false)}
                style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--text-3)', cursor: 'pointer', textAlign: 'center' }}
              >
                {t('login.wrongAddress')}
              </span>
            </div>

            <DevTokenRow value={devToken} onChange={setDevToken} onSubmit={verifyToken} />
          </>
        )}
      </div>
    </CenterShell>
  );
}

function DevTokenRow({
  value,
  onChange,
  onSubmit,
}: {
  value: string;
  onChange: (v: string) => void;
  onSubmit: (e: FormEvent) => void;
}) {
  const { t } = useT();
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <span style={divider}>
        <i style={dividerLine} />
        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 9, letterSpacing: '.1em', color: 'var(--text-3)' }}>
          {t('login.devDivider')}
        </span>
        <i style={dividerLine} />
      </span>
      <form onSubmit={onSubmit} style={{ display: 'flex', gap: 8 }}>
        <label style={{ ...fieldRow(false), flex: 1 }}>
          <Icon name="link" size={14} color="var(--text-3)" />
          <input
            placeholder={t('login.tokenPlaceholder')}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            style={nativeInput}
          />
        </label>
        <Button variant="ghost">{t('common.verify')}</Button>
      </form>
    </div>
  );
}

const card: CSSProperties = {
  background: '#FFFFFF',
  border: '1px solid #E4E4E7',
  borderRadius: 16,
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05), 0 1px 2px rgba(0, 0, 0, 0.03)',
  padding: 28,
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
};
const header: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 8,
  padding: '4px 0 2px',
  textAlign: 'center',
};
const title: CSSProperties = {
  fontFamily: 'var(--font-display)',
  fontSize: 20,
  fontWeight: 600,
  letterSpacing: '-0.025em',
  color: '#09090B',
};
function fieldRow(invalid: boolean): CSSProperties {
  return {
    display: 'flex',
    alignItems: 'center',
    gap: 9,
    height: 40,
    padding: '0 13px',
    background: '#FAFAFA',
    border: `1px solid ${invalid ? 'var(--danger)' : '#E4E4E7'}`,
    borderRadius: 10,
  };
}
const nativeInput: CSSProperties = {
  flex: 1,
  minWidth: 0,
  background: 'transparent',
  border: 'none',
  outline: 'none',
  color: '#09090B',
  fontSize: 13,
  fontFamily: 'var(--font-body)',
};
const errText: CSSProperties = { fontSize: 11.5, color: 'var(--danger)', paddingLeft: 2 };
const reassure: CSSProperties = {
  fontFamily: 'var(--font-mono)',
  fontSize: 9,
  color: 'var(--text-3)',
  textAlign: 'center',
  lineHeight: 1.7,
  letterSpacing: '.04em',
};
const mailBadge: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 56,
  height: 56,
  borderRadius: 17,
  background: 'var(--accent-tint)',
  border: '1px solid var(--line-accent)',
};
const expiryPill: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  fontFamily: 'var(--font-mono)',
  fontSize: 9.5,
  color: 'var(--text-3)',
  border: '1px solid var(--line-strong)',
  borderRadius: 999,
  padding: '5px 12px',
};
const divider: CSSProperties = { display: 'flex', alignItems: 'center', gap: 10, marginTop: 2 };
const dividerLine: CSSProperties = { flex: 1, height: 1, background: 'var(--line)' };
